import { useState } from 'react';
import { cn } from '@/lib/utils';
import { ChapterText, ContinueButton, PlayerOrb, Revelation, Whisper } from '../zen-ui';

const PLAYER = { x: 50, y: 76 } as const;

const WANDERERS = [
  { id: 'a', x: 22, y: 32, hue: 'blue', delay: '0s' },
  { id: 'b', x: 50, y: 14, hue: 'rose', delay: '-3s' },
  { id: 'c', x: 78, y: 34, hue: 'mint', delay: '-6s' },
] as const;

const HUES: Record<string, { core: string; glow: string }> = {
  blue: {
    core: 'radial-gradient(circle at 35% 30%, #eef4ff, #aec6f8 55%, #5b7bc9)',
    glow: 'rgba(174, 198, 248, 0.5)',
  },
  rose: {
    core: 'radial-gradient(circle at 35% 30%, #fff0f2, #f5bcc8 55%, #c97b92)',
    glow: 'rgba(245, 188, 200, 0.5)',
  },
  mint: {
    core: 'radial-gradient(circle at 35% 30%, #f0fff8, #b8e8cf 55%, #5fae87)',
    glow: 'rgba(184, 232, 207, 0.5)',
  },
};

/** Chapter V — follow the lights that move you. No algorithm, only choice. */
export function Constellation({ onComplete }: { onComplete: () => void }) {
  const [followed, setFollowed] = useState<string[]>([]);

  const follow = (id: string) =>
    setFollowed((f) => (f.includes(id) ? f : [...f, id]));

  return (
    <div className="relative z-10 flex min-h-dvh flex-col items-center justify-center gap-10 px-6 py-24 text-center">
      <ChapterText numeral="V · the follow" title="you choose your sky.">
        Other lights drift nearby. Follow the ones that move you — there is no
        algorithm here, only choice.
      </ChapterText>

      <div
        className="animate-zen-fade-in relative mx-auto h-80 w-full max-w-2xl"
        style={{ animationDelay: '700ms' }}
      >
        {/* constellation lines */}
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {WANDERERS.filter((w) => followed.includes(w.id)).map((w) => (
            <line
              key={w.id}
              x1={PLAYER.x}
              y1={PLAYER.y}
              x2={w.x}
              y2={w.y}
              stroke="rgba(253, 224, 171, 0.45)"
              strokeWidth={1.2}
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
              pathLength={1}
              strokeDasharray={1}
              strokeDashoffset={1}
              className="animate-zen-draw"
            />
          ))}
        </svg>

        {/* the traveler's light */}
        <div
          className="absolute"
          style={{
            left: `${PLAYER.x}%`,
            top: `${PLAYER.y}%`,
            transform: 'translate(-50%, -50%)',
          }}
        >
          <PlayerOrb size={64} />
        </div>

        {/* wandering lights */}
        {WANDERERS.map((w) => {
          const isFollowed = followed.includes(w.id);
          return (
            <button
              key={w.id}
              type="button"
              aria-label={`follow the ${w.hue} light`}
              onClick={() => follow(w.id)}
              className={cn(
                'animate-zen-float absolute rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-100/60',
                isFollowed && 'pointer-events-none',
              )}
              style={{ left: `${w.x}%`, top: `${w.y}%`, animationDelay: w.delay }}
            >
              <span
                className="block h-10 w-10 rounded-full transition-all duration-1000"
                style={{
                  background: HUES[w.hue].core,
                  boxShadow: isFollowed
                    ? `0 0 26px ${HUES[w.hue].glow}, 0 0 64px ${HUES[w.hue].glow}`
                    : `0 0 14px ${HUES[w.hue].glow}`,
                  opacity: isFollowed ? 1 : 0.75,
                }}
              />
              {isFollowed && (
                <span className="pointer-events-none absolute -inset-2 rounded-full border border-amber-100/30" />
              )}
            </button>
          );
        })}
      </div>

      {followed.length === 0 ? (
        <Whisper delay={1100}>touch a light to follow it</Whisper>
      ) : (
        <div className="flex flex-col items-center gap-8">
          <Revelation key={followed.length} delay={150}>
            {followed.length < WANDERERS.length
              ? 'your constellation has begun.'
              : 'a sky of your own making.'}
          </Revelation>
          <ContinueButton onClick={onComplete} label="rest" delay={1000} />
        </div>
      )}
    </div>
  );
}
